import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import {
  Eye,
  Download,
  Upload,
  Plus,
  Users,
  TrendingUp,
  TrendingDown,
  Building2,
  Calendar,
  Clock,
  ListChecks,
  XCircle,
  AlertCircle,
  Send,
  UserCheck,
  ArrowUpRight
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import Loader from '../../shared/components/ui/Loader';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import UploadResultsModal from '../components/ui/UploadResultsModal';
import { normalizeTemplateUploadResponse } from '../utils/templateUploadResponse';
import {
  createPreLeadList,
  downloadPreLeadTemplate,
  getAllCompanies,
  getPreLeadLists,
  uploadPreLeadTemplate
} from '../services/api/implementation/impl';
import { capitalizeWords } from '../utils/stringUtils';

function formatListDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const PreleadList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed } = useSidebar();
  const isMarketing = location.pathname.startsWith('/builder/marketing');
  const initialCompanyId =
    localStorage.getItem('companyId') || Cookies.get('companyId') || Cookies.get('company_id') || '';

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [companyId, setCompanyId] = useState(String(initialCompanyId || ''));
  const [listName, setListName] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const quickUploadInputRef = useRef(null);
  const [quickUploadListId, setQuickUploadListId] = useState(null);
  const lastUploadListIdRef = useRef(null);

  const [isUploadResultModalOpen, setIsUploadResultModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccesses, setUploadSuccesses] = useState([]);
  const [uploadDuplicates, setUploadDuplicates] = useState([]);

  const { data: companies = [], isPending: isCompaniesPending } = useQuery({
    queryKey: ['allCompaniesForPreleadList'],
    queryFn: getAllCompanies
  });

  const {
    data: listsData = { overall: null, preLeadList: [] },
    isLoading: isListsLoading,
    isError: isListsError,
    error: listsError
  } = useQuery({
    queryKey: ['preLeadLists'],
    queryFn: getPreLeadLists
  });

  const lists = listsData.preLeadList;
  const overallStats = listsData.overall;

  const companyNameById = useMemo(() => {
    const map = new Map();
    companies.forEach((c) => {
      map.set(c.id, c.company_name || c.name || `Company ${c.id}`);
    });
    return map;
  }, [companies]);

  const sortedLists = useMemo(
    () =>
      [...lists].sort((a, b) => {
        const ta = new Date(a.created_at).getTime();
        const tb = new Date(b.created_at).getTime();
        if (tb !== ta) return tb - ta;
        return (b.id ?? 0) - (a.id ?? 0);
      }),
    [lists]
  );

  useEffect(() => {
    if (companyId || companies.length === 0) return;
    const firstId = String(companies[0].id);
    setCompanyId(firstId);
  }, [companies, companyId]);

  const { mutate: submitPreLeadList, isPending: isCreatePending } = useMutation({
    mutationFn: (payload) => createPreLeadList(payload),
    onSuccess: () => {
      toast.success('List created successfully');
      setIsCreateModalOpen(false);
      setListName('');
      queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create list');
    }
  });

  const runDownload = async (listId, safeName) => {
    setDownloadingId(listId);
    try {
      const buffer = await downloadPreLeadTemplate(listId);
      if (!buffer || (buffer.byteLength !== undefined && buffer.byteLength === 0)) {
        toast.error('Downloaded file is empty.');
        return;
      }
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const slug = (safeName || `list_${listId}`).replace(/[^a-zA-Z0-9-_]/g, '_');
      saveAs(blob, `Prelead_${slug}_template.xlsx`);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const runUpload = async (listId, file) => {
    if (!file) return;
    lastUploadListIdRef.current = listId;
    setUploadingId(listId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadPreLeadTemplate(listId, formData);
      const { errors, results, duplicates } = normalizeTemplateUploadResponse(response);

      if (errors.length > 0) {
        setUploadErrors(errors);
        setUploadSuccesses(results);
        setUploadDuplicates(duplicates);
        setIsUploadResultModalOpen(true);
        queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', listId] });
      } else {
        toast.success('Template uploaded successfully');
        queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', listId] });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const closeUploadResultModal = () => {
    setIsUploadResultModalOpen(false);
    setUploadErrors([]);
    setUploadSuccesses([]);
    setUploadDuplicates([]);
  };

  const handleRejectPreleadDuplicates = (duplicatesToReject) => {
    setUploadDuplicates((prev) =>
      prev.filter((item) => !duplicatesToReject.some((rejected) => rejected.row === item.row))
    );
    if (
      uploadDuplicates.length === duplicatesToReject.length &&
      uploadErrors.length === 0
    ) {
      closeUploadResultModal();
    }
  };

  const handleRetryPreleadUpload = () => {
    const id = lastUploadListIdRef.current;
    closeUploadResultModal();
    if (id != null) {
      openQuickUploadPicker(id);
    }
  };

  const openQuickUploadPicker = (listId) => {
    setQuickUploadListId(listId);
    requestAnimationFrame(() => quickUploadInputRef.current?.click());
  };

  const onQuickUploadChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !quickUploadListId) return;
    runUpload(quickUploadListId, file);
    setQuickUploadListId(null);
  };

  const openListDetailPage = (list) => {
    if (!isMarketing) {
      try {
        localStorage.setItem('salesMainSecondaryTab', 'Prelead List');
      } catch {
        /* ignore */
      }
    }
    const base = isMarketing ? '/builder/marketing' : '/builder/sales';
    navigate(`${base}/prelead-lists/${list.id}`, {
      state: {
        listName: list.name,
        companyId: list.company_id,
        createdAt: list.created_at,
        source: list.source
      }
    });
  };

  const openAllLeadsPage = (filter = 'all') => {
    const base = isMarketing ? '/builder/marketing' : '/builder/sales';
    navigate(`${base}/prelead-lists/all-leads?filter=${encodeURIComponent(filter)}`);
  };

  const openCreateModal = () => {
    setListName('');
    if (!companyId && companies.length > 0) {
      setCompanyId(String(companies[0].id));
    }
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setListName('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const id = Number(companyId);
    if (!id) {
      toast.error('Please select a company');
      return;
    }
    const name = capitalizeWords(listName.trim());
    if (!name) {
      toast.error('Please enter a list name');
      return;
    }
    submitPreLeadList({ company_id: id, name });
  };

  const totalPreLeadLists = overallStats?.total_pre_lead_lists ?? 0;
  const totalPreLeads = overallStats?.total_pre_leads ?? 0;
  const interestedCount = overallStats?.interested_count ?? overallStats?.interested ?? 0;
  const notInterestedCount =
    overallStats?.not_interested_count ?? overallStats?.not_interested ?? 0;
  const pendingCount = overallStats?.pending_count ?? overallStats?.pending ?? 0;
  const qualifiedFromPreLeads = overallStats?.qualified_from_pre_leads ?? 0;
  const campaignFailedCount = overallStats?.campaign_failed_count ?? 0;
  const campaignNotSentCount = overallStats?.campaign_pre_lead_list_not_sent ?? 0;
  const campaignSentCount = overallStats?.whatsapp_campaign_sent_pre_lead ?? 0;

  const awaitingCount = Math.max(
    0,
    Number(pendingCount || totalPreLeads - interestedCount - notInterestedCount)
  );

  const showPageLoader = isListsLoading || isCompaniesPending;

  const statCards = [
    {
      label: 'Total Lists',
      value: totalPreLeadLists,
      icon: ListChecks,
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      valuColor: 'text-indigo-700',
      border: 'border-indigo-200'
    },
    {
      label: 'Total Leads',
      value: totalPreLeads,
      icon: Users,
      iconBg: 'bg-customBlue/10',
      iconColor: 'text-customBlue',
      valuColor: 'text-customBlue',
      border: 'border-customBlue/20',
      onClick: () => openAllLeadsPage('all')
    },
    {
      label: 'Potential Qualified',
      value: interestedCount,
      icon: TrendingUp,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      valuColor: 'text-green-700',
      border: 'border-green-200',
      onClick: () => openAllLeadsPage('potential-qualified')
    },
    {
      label: 'Qualified',
      value: qualifiedFromPreLeads,
      icon: UserCheck,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      valuColor: 'text-teal-700',
      border: 'border-teal-200',
      onClick: () => openAllLeadsPage('qualified')
    },
    {
      label: 'Not Interested',
      value: notInterestedCount,
      icon: TrendingDown,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      valuColor: 'text-red-600',
      border: 'border-red-200',
      onClick: () => openAllLeadsPage('not-interested')
    },
    {
      label: 'Awaiting Response',
      value: awaitingCount,
      icon: Clock,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-500',
      valuColor: 'text-amber-600',
      border: 'border-amber-200',
      onClick: () => openAllLeadsPage('awaiting')
    },
    {
      label: 'Message Sent',
      value: campaignSentCount,
      icon: Send,
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      valuColor: 'text-sky-700',
      border: 'border-sky-200'
    },
    {
      label: 'Campaign Not Sent',
      value: campaignNotSentCount,
      icon: AlertCircle,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      valuColor: 'text-orange-700',
      border: 'border-orange-200'
    },
    {
      label: 'Campaign Send Failed',
      value: campaignFailedCount,
      icon: XCircle,
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      valuColor: 'text-rose-700',
      border: 'border-rose-200'
    }
  ];

  const content = (
    <div className={`bg-gray-50 w-full min-h-full ${isMarketing ? 'mt-[4.5rem]' : 'mt-[4.4rem]'}`}>
      <Loader isVisible={showPageLoader} />
      <input
        ref={quickUploadInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        className="hidden"
        onChange={onQuickUploadChange}
      />

      <div className="px-3 sm:px-6 py-5 space-y-6 pb-10">
        {/* Stat cards — responsive multi-row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
          {statCards.map(({ label, value, icon: Icon, iconBg, iconColor, valuColor, border, onClick }) => {
            const baseClassName = `rounded-xl border ${border} bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-sm flex items-center gap-3`;
            if (onClick) {
              return (
                <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  className={`${baseClassName} relative pr-10 text-left transition-colors hover:bg-[#E9F5F6] focus:outline-none focus-visible:ring-2 focus-visible:ring-customBlue/30`}>
                  <div className={`hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
                    <Icon className={`h-5 w-5 ${iconColor}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
                    <p className={`mt-0.5 text-2xl sm:text-3xl font-bold leading-none ${valuColor}`}>{value}</p>
                  </div>
                  <span
                    className="absolute bottom-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-customBlue/10 text-customBlue"
                    aria-hidden>
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            }

            return (
              <div key={label} className={baseClassName}>
                <div className={`hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
                  <Icon className={`h-5 w-5 ${iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
                  <p className={`mt-0.5 text-2xl sm:text-3xl font-bold leading-none ${valuColor}`}>{value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Section label */}
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-700">All Lists</h2>
          {!isListsLoading && !isListsError && sortedLists.length > 0 && (
            <span className="rounded-full bg-customBlue/10 px-2 py-0.5 text-xs font-semibold text-customBlue">
              {sortedLists.length}
            </span>
          )}
        </div>

        {isListsError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {listsError?.response?.data?.message || listsError?.message || 'Could not load lists.'}
          </div>
        )}

        {/* Empty state */}
        {!isListsLoading && !isListsError && sortedLists.length === 0 && (
          <div className="w-full max-w-2xl rounded-2xl border border-customBlue/20 bg-white p-6 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="prelead-inline-company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <select
                  id="prelead-inline-company"
                  value={companyId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setCompanyId(next);
                    if (next) {
                      localStorage.setItem('companyId', next);
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue">
                  <option value="">Select company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name || company.name || `Company ${company.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="prelead-inline-name" className="block text-sm font-medium text-gray-700 mb-1">
                  List name
                </label>
                <input
                  id="prelead-inline-name"
                  type="text"
                  value={listName}
                  onChange={(e) => setListName(capitalizeWords(e.target.value))}
                  placeholder="e.g. Sept Campaign Contacts"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue"
                />
              </div>
              <div className="pt-2 flex justify-start">
                <Button type="submit" disabled={isCreatePending}>
                  {isCreatePending ? 'Creating…' : 'Create list'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* List grid — shown when lists exist (includes Create card as first item) */}
        {!isListsLoading && !isListsError && sortedLists.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-3 sm:gap-4">
            {/* Create a list — first card */}
            <button
              type="button"
              onClick={openCreateModal}
              className="group flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-customBlue/40 bg-white px-4 py-8 text-customBlue shadow-sm hover:border-customBlue hover:bg-[#E9F5F6] transition-all duration-200 min-h-[160px]">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-customBlue/10 group-hover:bg-customBlue/20 transition-colors">
                <Plus className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold whitespace-nowrap">Create a list</span>
            </button>

            {sortedLists.map((list) => {
              const busy = downloadingId === list.id || uploadingId === list.id;
              const companyName = companyNameById.get(list.company_id) ?? `ID ${list.company_id}`;
              return (
                <article
                  key={list.id}
                  className="group rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-customBlue/40 transition-all duration-200 flex flex-col overflow-hidden">
                  {/* Card top accent */}
                  <div className="h-1 w-full bg-gradient-to-r from-customBlue to-customBlue/50 rounded-t-xl" />

                  <div className="p-4 flex-1 flex flex-col gap-3">
                    {/* Name */}
                    <h3
                      className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-customBlue transition-colors cursor-pointer"
                      onClick={() => openListDetailPage(list)}>
                      {list.name}
                    </h3>

                    {/* Meta */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Building2 className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span className="truncate font-medium text-gray-700" title={companyName}>
                          {companyName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span>{formatListDate(list.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Users className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span className="font-medium text-gray-700">
                          {list.pre_lead_count ?? 0} contact{(list.pre_lead_count ?? 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="border-t border-gray-100 px-3 py-2.5 flex gap-1.5">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => openListDetailPage(list)}
                      title="View"
                      className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-customBlue/30 bg-[#E9F5F6] px-2 py-1.5 text-xs font-semibold text-customBlue hover:bg-customBlue/20 disabled:opacity-50 transition-colors">
                      <Eye className="h-3.5 w-3.5 shrink-0" />
                      <span>View</span>
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => runDownload(list.id, list.name)}
                      title="Download template"
                      className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">
                      <Download className="h-3.5 w-3.5 shrink-0" />
                      <span className="hidden sm:inline">
                        {downloadingId === list.id ? '…' : 'Download'}
                      </span>
                      {downloadingId === list.id && <span className="sm:hidden">…</span>}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => openQuickUploadPicker(list.id)}
                      title="Upload contacts"
                      className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-customBlue px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
                      <Upload className="h-3.5 w-3.5 shrink-0" />
                      <span>{uploadingId === list.id ? '…' : 'Upload'}</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <UploadResultsModal
        isOpen={isUploadResultModalOpen}
        onClose={closeUploadResultModal}
        uploadSuccesses={uploadSuccesses}
        uploadErrors={uploadErrors}
        uploadDuplicates={uploadDuplicates}
        onAcceptDuplicates={() => {}}
        onRejectDuplicates={handleRejectPreleadDuplicates}
        isProcessingDuplicates={false}
        onRetryUpload={handleRetryPreleadUpload}
        fileEntityLabel="preleads"
        duplicateActionsEnabled={false}
      />

      {isCreateModalOpen && (
        <Modal name="Create prelead list" onClose={closeCreateModal} width={40}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="prelead-list-company" className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              <select
                id="prelead-list-company"
                value={companyId}
                onChange={(e) => {
                  const next = e.target.value;
                  setCompanyId(next);
                  if (next) {
                    localStorage.setItem('companyId', next);
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue">
                <option value="">Select company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name || company.name || `Company ${company.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="prelead-list-name" className="block text-sm font-medium text-gray-700 mb-1">
                List name
              </label>
              <input
                id="prelead-list-name"
                type="text"
                value={listName}
                onChange={(e) => setListName(capitalizeWords(e.target.value))}
                placeholder="e.g. Sept Campaign Contacts"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue"
              />
            </div>
            <div className="pt-2 flex justify-end gap-3">
              <Button type="button" displayType="secondary" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatePending}>
                {isCreatePending ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );

  if (isMarketing) {
    return (
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Marketing > Prelead List' }} />
        </div>
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          {content}
        </div>
      </Layout>
    );
  }

  return content;
};

export default PreleadList;
